import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { API_BASE_URL } from '../config';

const VerificationAssessmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const candidateData = location.state;

  // Core session state
  const [verificationId, setVerificationId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [assessmentComplete, setAssessmentComplete] = useState(false);

  // TTS/STT state
  const recognitionRef = useRef(null);
  const [transcript, setTranscript] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showingIntro, setShowingIntro] = useState(true);
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [micMuted, setMicMuted] = useState(false); // Start unmuted

  // MCQ state
  const [selectedOption, setSelectedOption] = useState(null);

  // Situational text state
  const [textAnswer, setTextAnswer] = useState('');

  // Single video ref for both display and proctoring
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  
  // Proctoring state
  const [faceDetectionStatus, setFaceDetectionStatus] = useState('checking');
  const [faceCount, setFaceCount] = useState(0);
  const [warningBanner, setWarningBanner] = useState({ show: false, message: '' });
  const [lastWarningTime, setLastWarningTime] = useState(0);
  const [gracePeriodActive, setGracePeriodActive] = useState(true);
  const proctoringIntervalRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [warningsCount, setWarningsCount] = useState(0);

  useEffect(() => {
    if (!candidateData) {
      navigate('/verification-welcome');
      return;
    }

    // Initialize Speech Recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      console.log('✅ Speech recognition initialized');
    } else {
      console.warn('⚠️ Speech recognition not available');
    }

    console.log('🚀 Initializing verification session...');
    initializeSession();

    return () => {
      console.log('🧹 Cleaning up verification session...');
      cleanup();
    };
  }, [candidateData, navigate]);

  const cleanup = () => {
    stopProctoring();
    stopSpeechRecognition();
    stopTTS();
    stopCamera();
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        recognitionRef.current.onend = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
      } catch (e) {
        console.log('Recognition already stopped');
      }
    }
    setIsListening(false);
  };

  const stopTTS = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
  };

  const initializeSession = async () => {
    try {
      setLoading(true);
      console.log('📡 Starting verification session for:', candidateData);

      // Get token from the proper location
      const currentRole = localStorage.getItem('dutlaunch_current_session');
      const token = currentRole ? localStorage.getItem(`dutlaunch_${currentRole}_token`) : null;
      
      if (!token) {
        console.error('❌ No authentication token found');
        alert('Please log in to continue');
        navigate('/login');
        return;
      }
      
      console.log('✅ Token found for role:', currentRole);

      console.log('📤 Sending verification start request...');
      const response = await fetch(`${API_BASE_URL}/verification/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          candidate_id: candidateData.candidateId,
          candidate_email: candidateData.candidateEmail,
          candidate_name: candidateData.candidateName
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Verification start failed:', errorData);
        throw new Error(errorData.detail || 'Failed to start verification');
      }

      const data = await response.json();
      console.log('✅ Verification session started:', data);
      setVerificationId(data.verification_id);
      setQuestions(data.questions);

      // Start camera
      console.log('🎥 Starting camera...');
      await startCamera();

      // Speak welcome message
      setTimeout(() => {
        const welcomeMessage = `Hello ${candidateData.candidateName}, welcome to your profile verification assessment. This assessment will consist of ${data.questions.length} questions to verify your skills and experience. When you're ready, click the button to begin.`;
        console.log('🔊 Speaking welcome message');
        speakMessage(welcomeMessage);
      }, 1000);

    } catch (error) {
      console.error('❌ Error starting verification:', error);
      alert(`Failed to start verification: ${error.message}`);
      navigate('/verification-welcome');
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      console.log('🎥 Requesting camera access...');
      
      // Request camera AND microphone access with proper constraints
      const constraints = {
        video: { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: { 
          echoCancellation: true, 
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('✅ Camera access granted');
      console.log('Media stream obtained:', stream);
      
      cameraStreamRef.current = stream;
      
      // Enable audio tracks by default
      const audioTracks = stream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = true;
        console.log('Audio track enabled:', track.label);
      });
      
      // Single video element for both display and proctoring
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded');
        };
        
        // Try to play the video
        try {
          await videoRef.current.play();
          console.log('✅ Camera video playing');
        } catch (playError) {
          console.log('Autoplay prevented, but video should still work:', playError);
        }
      }
      
    } catch (err) {
      console.error('❌ Error starting camera:', err);
      
      let errorMessage = 'Unable to access camera/microphone. ';
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera and microphone access to continue.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No camera or microphone found. Please connect devices to continue.';
      } else if (err.name === 'NotReadableError') {
        errorMessage += 'Camera is already in use by another application.';
      } else {
        errorMessage += err.message;
      }
      
      alert(errorMessage);
    }
  };

  const speakMessage = (text) => {
    return new Promise((resolve) => {
      if (!('speechSynthesis' in window)) {
        console.error('TTS not supported');
        resolve();
        return;
      }

      stopTTS(); // Stop any ongoing speech
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;
      
      utterance.onstart = () => {
        console.log('🔊 TTS started');
        setIsSpeaking(true);
      };
      
      utterance.onend = () => {
        console.log('🔇 TTS ended');
        setIsSpeaking(false);
        resolve();
        
        // Auto-start listening for voice questions
        if (currentQuestionIndex >= 0 && questions[currentQuestionIndex]?.question_type === 'voice') {
          setTimeout(() => {
            startListeningWindow();
          }, 500);
        }
      };
      
      utterance.onerror = (err) => {
        console.error('❌ TTS error:', err);
        setIsSpeaking(false);
        resolve();
      };
      
      window.speechSynthesis.speak(utterance);
    });
  };

  const startListeningWindow = () => {
    if (!recognitionRef.current) {
      console.error('❌ Speech recognition not available');
      alert('Speech recognition is not available in your browser. Please use Chrome or Edge.');
      return;
    }

    // Stop any existing recognition first
    stopSpeechRecognition();

    console.log('🎤 Starting listening window...');
    
    const maxSeconds = 120; // 2 minutes max
    const startedAt = Date.now();
    let forceStop = false;
    let finals = [];
    let latestInterim = '';
    
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    
    const tickTimer = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= maxSeconds * 1000) {
        forceStop = true;
        stopSpeechRecognition();
        clearInterval(tickTimer);
      }
    }, 1000);
    
    recognitionRef.current.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result || !result[0]) continue;
        
        const text = (result[0].transcript || '').trim();
        if (!text) continue;
        
        if (result.isFinal) {
          finals.push(text);
          latestInterim = '';
        } else {
          latestInterim = text;
        }
      }
      
      const combined = (finals.join(' ') + ' ' + latestInterim).trim();
      setTranscript(combined);
    };
    
    recognitionRef.current.onstart = () => {
      console.log('✅ Speech recognition started');
      setIsListening(true);
    };
    
    recognitionRef.current.onend = () => {
      console.log('🔇 Speech recognition ended');
      setIsListening(false);
      clearInterval(tickTimer);
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('❌ Speech recognition error:', event.error);
      
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        forceStop = true;
        clearInterval(tickTimer);
        setTranscript('');
        alert('Microphone access denied. Please allow microphone access and refresh the page.');
        return;
      }
      
      if (event.error === 'no-speech') {
        console.log('⚠️ No speech detected, continuing...');
        return;
      }
    };
    
    try {
      recognitionRef.current.start();
    } catch (e) {
      console.error('❌ Failed to start recognition:', e);
      clearInterval(tickTimer);
      alert('Failed to start speech recognition. Please check your microphone permissions.');
    }
  };

  const clearCurrentAnswer = () => {
    setTranscript('');
    setSelectedOption(null);
    setTextAnswer('');
    stopSpeechRecognition();
    stopTTS();
  };

  const handleSkipQuestion = async () => {
    clearCurrentAnswer();
    await submitAnswer('Skipped by candidate', null, null);
    moveToNextQuestion();
  };

  const handleNextQuestion = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    
    if (currentQuestion.question_type === 'voice') {
      stopSpeechRecognition();
      const answer = transcript.trim() || 'No answer provided';
      await submitAnswer(answer, null, answer);
    } else if (currentQuestion.question_type === 'mcq') {
      if (!selectedOption) {
        alert('Please select an option before proceeding');
        return;
      }
      await submitAnswer(null, selectedOption, null);
    } else if (currentQuestion.question_type === 'situational') {
      if (!textAnswer.trim()) {
        alert('Please provide an answer before proceeding');
        return;
      }
      await submitAnswer(textAnswer, null, null);
    }
    
    clearCurrentAnswer();
    moveToNextQuestion();
  };

  const submitAnswer = async (answerText, answerOption, answerTranscript) => {
    try {
      const currentRole = localStorage.getItem('dutlaunch_current_session');
      const token = currentRole ? localStorage.getItem(`dutlaunch_${currentRole}_token`) : null;
      const currentQuestion = questions[currentQuestionIndex];

      await fetch(`${API_BASE_URL}/verification/submit-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          verification_id: verificationId,
          question_number: currentQuestion.question_number,
          question_type: currentQuestion.question_type,
          answer_text: answerText,
          answer_option: answerOption,
          answer_transcript: answerTranscript
        })
      });

    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  const moveToNextQuestion = () => {
    clearCurrentAnswer();
    
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      
      setTimeout(() => {
        const nextQuestion = questions[nextIndex];
        const questionText = `Question ${nextQuestion.question_number}: ${nextQuestion.question_text}`;
        speakMessage(questionText);
      }, 1000);
    } else {
      handleCompleteAssessment();
    }
  };

  const issueWarning = (message) => {
    const now = Date.now();
    const WARNING_COOLDOWN = 10000;
    
    if (now - lastWarningTime < WARNING_COOLDOWN) {
      return;
    }
    
    setLastWarningTime(now);
    setWarningsCount(prev => prev + 1);
    
    setWarningBanner({ show: true, message });
    
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    warningTimeoutRef.current = setTimeout(() => {
      setWarningBanner({ show: false, message: '' });
    }, 8000);
  };

  const analyzeFrame = async () => {
    if (!videoRef.current || !assessmentStarted || !verificationId) {
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      if (canvas.width === 0 || canvas.height === 0) {
        return;
      }
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      const frameData = canvas.toDataURL('image/jpeg', 0.8);
      
      const response = await fetch(`${API_BASE_URL}/api/interview/analyze-frame`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interview_id: verificationId,
          question_number: currentQuestionIndex + 1,
          frame_data: frameData
        })
      });

      if (response.ok) {
        const data = await response.json();
        const analysis = data.analysis;
        
        if (!analysis) return;
        
        setFaceCount(analysis.face_count || 0);
        
        // Only show warnings after grace period
        if (!gracePeriodActive) {
          if (analysis.warnings && analysis.warnings.length > 0) {
            const warning = analysis.warnings[0];
            let message = '';
            
            switch(warning) {
              case 'no_face':
                message = '⚠️ No face detected. Please position yourself in front of the camera.';
                setFaceDetectionStatus('no_face');
                break;
              case 'multiple_faces':
                message = '⚠️ Multiple faces detected. Only you should be visible in the frame.';
                setFaceDetectionStatus('multiple_faces');
                break;
              case 'flat_surface':
                message = '⚠️ Face covered or object detected. Please ensure your face is clearly visible.';
                setFaceDetectionStatus('no_face');
                break;
              default:
                message = '⚠️ Face detection issue. Please ensure you are clearly visible.';
            }
            
            if (message) {
              issueWarning(message);
            }
          } else if (analysis.face_count === 1) {
            setFaceDetectionStatus('ok');
          }
          
          if (analysis.device_detected) {
            issueWarning('⚠️ Phone or device detected. Please remove it from the camera view.');
          }
        } else {
          // During grace period, just update status without warnings
          if (analysis.face_count === 1) {
            setFaceDetectionStatus('ok');
          } else if (analysis.face_count === 0) {
            setFaceDetectionStatus('checking');
          } else {
            setFaceDetectionStatus('checking');
          }
        }
      }
    } catch (err) {
      console.error('Error analyzing frame:', err);
    }
  };

  const startProctoring = () => {
    setGracePeriodActive(true);
    setTimeout(() => {
      setGracePeriodActive(false);
    }, 10000);
    
    proctoringIntervalRef.current = setInterval(() => {
      analyzeFrame();
    }, 3000);
  };

  const stopProctoring = () => {
    if (proctoringIntervalRef.current) {
      clearInterval(proctoringIntervalRef.current);
      proctoringIntervalRef.current = null;
    }
  };

  const startFirstQuestion = () => {
    setShowingIntro(false);
    setAssessmentStarted(true);
    setCurrentQuestionIndex(0);
    clearCurrentAnswer();
    
    if (verificationId) {
      startProctoring();
    }
    
    setTimeout(() => {
      if (questions.length > 0) {
        const questionText = `Question ${questions[0].question_number}: ${questions[0].question_text}`;
        speakMessage(questionText);
      }
    }, 500);
  };

  const toggleMic = () => {
    if (cameraStreamRef.current) {
      const audioTracks = cameraStreamRef.current.getAudioTracks();
      const newMutedState = !micMuted;
      audioTracks.forEach(track => {
        track.enabled = !newMutedState;
      });
      setMicMuted(newMutedState);
      console.log(`🎤 Microphone ${newMutedState ? 'muted' : 'unmuted'}`);
    }
  };

  const handleCompleteAssessment = async () => {
    try {
      setLoading(true);
      cleanup();

      const currentRole = localStorage.getItem('dutlaunch_current_session');
      const token = currentRole ? localStorage.getItem(`dutlaunch_${currentRole}_token`) : null;
      await fetch(`${API_BASE_URL}/verification/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          verification_id: verificationId,
          warnings_count: warningsCount
        })
      });

      setAssessmentComplete(true);
      navigate('/verification-complete');

    } catch (error) {
      console.error('Error completing assessment:', error);
      alert('Failed to complete assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background-light dark:bg-background-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {assessmentComplete ? 'Completing assessment...' : 'Starting verification...'}
          </p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = currentQuestionIndex >= 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  return (
    <div className="relative flex h-screen w-full overflow-hidden bg-white dark:bg-gray-900">
      {/* Warning Banner */}
      {warningBanner.show && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-yellow-500 text-white px-4 py-3 text-center font-semibold animate-pulse">
          {warningBanner.message}
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Camera Feed */}
        <div className="w-[420px] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          {/* Video Feed */}
          <div className="relative bg-black flex items-center justify-center overflow-hidden" style={{ height: '315px' }}>
            {/* Face Detection Badge */}
            <div className="absolute top-3 right-3 z-10">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold backdrop-blur-sm ${
                faceDetectionStatus === 'ok' 
                  ? 'bg-green-500/90 text-white'
                  : 'bg-yellow-500/90 text-white'
              }`}>
                <Icon 
                  name={faceDetectionStatus === 'ok' ? "face" : "face_retouching_off"} 
                  size={14} 
                />
                <span>{faceDetectionStatus === 'ok' ? 'Face Detected' : 'Checking...'}</span>
              </div>
            </div>

            {/* Warning Count */}
            {warningsCount > 0 && (
              <div className="absolute top-3 left-3 z-10">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500/90 text-white backdrop-blur-sm">
                  <Icon name="warning" size={14} />
                  <span>{warningsCount} Warnings</span>
                </div>
              </div>
            )}

            {/* Center Guide */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-40 h-52 border-2 border-dashed border-white/40 rounded-lg"></div>
            </div>
            
            {/* Single Webcam Feed - Used for both display and proctoring */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
            
            {/* Overlay when no camera */}
            {!cameraStreamRef.current && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
                <Icon name="videocam_off" size={48} className="text-gray-500 mb-2" />
                <p className="text-gray-400 text-sm">Camera starting...</p>
              </div>
            )}
          </div>

          {/* Monitoring Status */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Icon name="monitor_heart" size={18} className="text-blue-600" />
              Monitoring Status
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {/* Face Detection */}
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon 
                  name="face" 
                  size={24} 
                  className={faceDetectionStatus === 'ok' ? 'text-green-500 mx-auto mb-1' : 'text-yellow-500 mx-auto mb-1'} 
                />
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Face</div>
                <div className={`text-xs font-bold ${faceDetectionStatus === 'ok' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {faceDetectionStatus === 'ok' ? 'OK' : 'Checking'}
                </div>
              </div>

              {/* Eye Tracking */}
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon 
                  name="visibility" 
                  size={24} 
                  className="text-green-500 mx-auto mb-1" 
                />
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Eye Track</div>
                <div className="text-xs font-bold text-green-600">
                  Focused
                </div>
              </div>

              {/* Device Status */}
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon 
                  name={!micMuted ? "mic" : "mic_off"} 
                  size={24} 
                  className={!micMuted ? 'text-green-500 mx-auto mb-1' : 'text-gray-400 mx-auto mb-1'} 
                />
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Device</div>
                <div className={`text-xs font-bold ${!micMuted ? 'text-green-600' : 'text-gray-500'}`}>
                  {!micMuted ? 'Active' : 'Muted'}
                </div>
              </div>
            </div>
          </div>

          {/* Verification Session Info */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Icon name="verified_user" size={18} className="text-blue-600" />
              Verification Session
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">Role:</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">Self</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">Type:</span>
                <span className="text-sm font-semibold text-blue-600">Verification</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">Duration:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {questions.length > 0 ? `~${Math.ceil(questions.length * 1.5)} min` : '20 min'}
                </span>
              </div>
            </div>
          </div>

          {/* Recording Status */}
          <div className="p-4 bg-gray-50 dark:bg-gray-750">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">Recording</span>
              </div>
              <span className="text-gray-600 dark:text-gray-400 font-semibold">
                {currentQuestionIndex >= 0 ? `${currentQuestionIndex + 1}/${questions.length}` : '0/0'}
              </span>
            </div>
          </div>

          {/* Mic Toggle Button */}
          <div className="p-4">
            <button
              onClick={toggleMic}
              className={`w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${
                micMuted 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              <Icon name={micMuted ? "mic_off" : "mic"} size={20} />
              {micMuted ? 'Unmute Microphone' : 'Microphone Active'}
            </button>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-900">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="verified_user" size={24} className="text-blue-600" />
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  Profile Verification Assessment
                </h1>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${faceDetectionStatus === 'ok' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <span className="text-gray-600 dark:text-gray-400">
                  {faceDetectionStatus === 'ok' ? 'Verified' : 'Verifying'}
                </span>
              </div>
            </div>
          </div>

          {showingIntro ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
              <div className="max-w-2xl text-center">
                <div className="w-24 h-24 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="verified_user" size={48} className="text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Ready to Begin Your Verification?
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                  This assessment consists of {questions.length} questions designed to verify your profile authenticity.
                  <br />
                  <span className="text-sm mt-2 block">Click "Start Interview" to begin</span>
                </p>
                
                {/* Conversation Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 mb-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="chat" size={20} className="text-blue-600" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Conversation</h3>
                  </div>
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <Icon name="chat_bubble_outline" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Ready to begin your interview
                        <br />
                        <span className="text-xs">Click "Start Interview" to begin</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Start Button */}
                <button
                  onClick={startFirstQuestion}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Icon name="phone" size={20} />
                  Start Interview
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col p-6 overflow-auto">
              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {Math.round(progress)}% Complete
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Question Content */}
              {currentQuestion && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentQuestion.question_type === 'voice' ? 'bg-blue-100 dark:bg-blue-900/20' :
                      currentQuestion.question_type === 'mcq' ? 'bg-green-100 dark:bg-green-900/20' :
                      'bg-purple-100 dark:bg-purple-900/20'
                    }`}>
                      <Icon 
                        name={
                          currentQuestion.question_type === 'voice' ? 'mic' :
                          currentQuestion.question_type === 'mcq' ? 'quiz' : 'psychology'
                        }
                        size={20}
                        className={
                          currentQuestion.question_type === 'voice' ? 'text-blue-600' :
                          currentQuestion.question_type === 'mcq' ? 'text-green-600' :
                          'text-purple-600'
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <span className={`text-xs font-semibold uppercase ${
                        currentQuestion.question_type === 'voice' ? 'text-blue-600' :
                        currentQuestion.question_type === 'mcq' ? 'text-green-600' :
                        'text-purple-600'
                      }`}>
                        {currentQuestion.question_type === 'voice' ? 'Voice Question' :
                         currentQuestion.question_type === 'mcq' ? 'Multiple Choice' : 'Situational'}
                      </span>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                        {currentQuestion.question_text}
                      </h2>
                    </div>
                  </div>

                  {/* Voice Question */}
                  {currentQuestion.question_type === 'voice' && (
                    <div className="mt-6">
                      {/* TTS Speaking Indicator */}
                      {isSpeaking && (
                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center gap-2">
                            <Icon name="volume_up" size={20} className="text-blue-600 animate-pulse" />
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">AI is speaking...</span>
                          </div>
                        </div>
                      )}

                      {/* Mic Wave Animation */}
                      {isListening && (
                        <div className="flex items-center gap-3 mb-4 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className="w-1 bg-red-500 rounded-full animate-pulse"
                                style={{
                                  height: `${12 + Math.random() * 20}px`,
                                  animationDelay: `${i * 0.1}s`,
                                  animationDuration: '0.6s'
                                }}
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-red-600 dark:text-red-400">Listening... Speak now</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Manual Start Listening Button */}
                      {!isListening && !isSpeaking && (
                        <div className="mb-4">
                          <button
                            onClick={startListeningWindow}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                          >
                            <Icon name="mic" size={20} />
                            Start Speaking
                          </button>
                        </div>
                      )}
                      
                      {/* STT Transcript Display */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 min-h-[150px] border-2 border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="mic" size={16} className="text-blue-600" />
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            Your Answer
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                          {transcript || (isListening ? 'Start speaking...' : isSpeaking ? 'AI is speaking...' : 'Click "Start Speaking" to begin')}
                        </p>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleSkipQuestion}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Icon name="skip_next" size={20} />
                          Skip Question
                        </button>
                        <button
                          onClick={handleNextQuestion}
                          disabled={!transcript.trim()}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Icon name="arrow_forward" size={20} />
                          Next Question
                        </button>
                      </div>
                      
                      {/* Info Text */}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
                        <Icon name="info" size={14} />
                        Click "Next Question" when done speaking or "Skip" to move on
                      </p>
                    </div>
                  )}

                  {/* MCQ */}
                  {currentQuestion.question_type === 'mcq' && currentQuestion.options && (
                    <div className="mt-6 space-y-3">
                      {Object.entries(currentQuestion.options).map(([key, value]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedOption(key)}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                            selectedOption === key
                              ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                          }`}
                        >
                          <span className="font-semibold text-blue-600 mr-3">{key}.</span>
                          <span className="text-gray-900 dark:text-white">{value}</span>
                        </button>
                      ))}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleSkipQuestion}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Icon name="skip_next" size={20} />
                          Skip Question
                        </button>
                        <button
                          onClick={handleNextQuestion}
                          disabled={!selectedOption}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Icon name="arrow_forward" size={20} />
                          Next Question
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
                        <Icon name="info" size={14} />
                        Select an option and click "Next Question" or "Skip" to continue
                      </p>
                    </div>
                  )}

                  {/* Situational */}
                  {currentQuestion.question_type === 'situational' && (
                    <div className="mt-6">
                      <textarea
                        value={textAnswer}
                        onChange={(e) => setTextAnswer(e.target.value)}
                        placeholder="Type your detailed response here..."
                        className="w-full h-40 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-blue-600 focus:outline-none dark:bg-gray-700 dark:text-white resize-none"
                      />
                      
                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleSkipQuestion}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Icon name="skip_next" size={20} />
                          Skip Question
                        </button>
                        <button
                          onClick={handleNextQuestion}
                          disabled={!textAnswer.trim()}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Icon name="arrow_forward" size={20} />
                          Next Question
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
                        <Icon name="info" size={14} />
                        Type your answer and click "Next Question" or "Skip" to continue
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationAssessmentPage;
